import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getSessionId } from '../lib/tracking';

/** Journalise le consentement côté serveur pour preuve CNIL. */
async function persistConsentsToServer(state: {
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
}): Promise<void> {
  if (typeof window === 'undefined') return;
  const sessionId = getSessionId();
  if (!sessionId) return;
  try {
    await fetch('/api/leads/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        consents: [
          { purpose: 'analytics', granted: state.analytics },
          { purpose: 'marketing', granted: state.marketing },
        ],
      }),
    });
  } catch {
    // Silently fail — le journal de consentement ne doit pas bloquer l'UX
  }
}

export const CONSENT_VERSION = '1.0';

export interface ConsentState {
  /** Version du consentement — bump = re-affichage du bandeau */
  version: string;
  /** L'utilisateur a interagi avec le bandeau */
  hasResponded: boolean;
  /** Catégories consenties */
  essentials: true; // toujours true, non modifiable
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
  /** Panneau Sheet personnalisation ouvert */
  customizeOpen: boolean;
  /** Actions */
  acceptAll: () => void;
  rejectAll: () => void;
  setCategories: (prefs: {
    preferences?: boolean;
    analytics?: boolean;
    marketing?: boolean;
  }) => void;
  openCustomize: () => void;
  closeCustomize: () => void;
  reset: () => void;
}

export const useConsent = create<ConsentState>()(
  persist(
    (set) => ({
      version: CONSENT_VERSION,
      hasResponded: false,
      essentials: true,
      preferences: false,
      analytics: false,
      marketing: false,
      customizeOpen: false,

      acceptAll: () => {
        const next = {
          hasResponded: true,
          preferences: true,
          analytics: true,
          marketing: true,
          customizeOpen: false,
          version: CONSENT_VERSION,
        };
        set(next);
        void persistConsentsToServer({ preferences: true, analytics: true, marketing: true });
      },

      rejectAll: () => {
        set({
          hasResponded: true,
          preferences: false,
          analytics: false,
          marketing: false,
          customizeOpen: false,
          version: CONSENT_VERSION,
        });
        void persistConsentsToServer({ preferences: false, analytics: false, marketing: false });
      },

      setCategories: (prefs) => {
        set((state) => {
          const next = {
            ...state,
            ...prefs,
            hasResponded: true,
            customizeOpen: false,
            version: CONSENT_VERSION,
          };
          void persistConsentsToServer({
            preferences: next.preferences,
            analytics: next.analytics,
            marketing: next.marketing,
          });
          return next;
        });
      },

      openCustomize: () => set({ customizeOpen: true }),
      closeCustomize: () => set({ customizeOpen: false }),

      reset: () =>
        set({
          hasResponded: false,
          preferences: false,
          analytics: false,
          marketing: false,
          customizeOpen: false,
          version: CONSENT_VERSION,
        }),
    }),
    {
      name: 'sef_consent',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        version: state.version,
        hasResponded: state.hasResponded,
        preferences: state.preferences,
        analytics: state.analytics,
        marketing: state.marketing,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && state.version !== CONSENT_VERSION) {
          state.reset();
        }
      },
    },
  ),
);
